/** I13 as a check on the shapes *below* the root, not just on the root.
 *
 * `schema.ts` closes the key set of `GameState`, and that is what makes adding a
 * root field fail to compile and then fail every committed fixture. A nested
 * shape had neither guard, and the first real migration proved it matters: when
 * `PlayerState.townlessDays` was added, a format-1 file whose players lacked the
 * field would have passed `validateSaveTree` unchanged, `decodeTree` would have
 * handed the kernel a `PlayerState` with `townlessDays === undefined`, and I6
 * ("no undefined values in state") would have been broken silently. Only the
 * author remembering to write `migrations/0002.ts` caught it.
 *
 * So every persisted record shape gets the same two guards the root has:
 *
 *  1. **A compile-time check.** Every list below is declared through `keysOf`,
 *     which does not compile unless the list is exactly the shape's key set.
 *     Add a field to `PlayerState` and this file goes red on the same edit.
 *  2. **A runtime check.** `findShapeIssues` walks the declared paths of a
 *     parsed tree and refuses a file whose records do not carry exactly these
 *     keys. So once the list is updated, every committed fixture goes red until
 *     `migrations/NNNN.ts` puts the field into the old files too.
 *
 * What is deliberately *not* listed: records whose keys are content data rather
 * than code (an object's `payload`, a town's `available`, a hero's `artifacts`,
 * a fog memory snapshot, the free-form `data` of a resume token), and the
 * discriminated unions (`InteractionPrompt`, `InteractionResponse`,
 * `BattleOutcome`) whose key set legitimately differs per `kind`. Those are the
 * `schema.ts` deny-list sweep's business, not this file's.
 */

import type {
  AdventureMapState, ArmySlot, ArmyState, CaravanState, FogState, GameState, HeroState,
  MapObjectState, PendingInteraction, PlayerState, PrimarySkills, Resources,
  ResumeToken, SecondarySkillState, TownState, TurnState,
} from "../state.js";
import { RESOURCES } from "../state.js";
import type { Modifier } from "../modifiers.js";
import type { RngStreamState } from "../rng/pcg32.js";
import { RNG_STREAMS } from "../rng/streams.js";
import type {
  BattleHeroState, BattleState, Obstacle, StackStore,
} from "../battle/state.js";

// ------------------------------------------------------ the compile-time half

type Missing<Shape, Keys extends string> = Exclude<Extract<keyof Shape, string>, Keys>;

/**
 * Declares the key list of one persisted shape, and refuses to compile unless
 * the list is exactly that shape's key set.
 *
 * A key the shape does not have fails on the argument (`"foo" is not assignable
 * to keyof HeroState`). A key the shape has and the list does not fails on the
 * *call* - "expected 2 arguments, but got 1", where the second parameter is
 * named `missingFromTheKeyList` and its type is the field that was forgotten.
 * Either way the error lands on the list, which is the line that has to change.
 *
 * A rest parameter rather than an intersection because it leaves `K` inferred
 * from the literal alone: the list keeps its tuple type, so it can still be
 * spread into the root-level checks and read by the runtime sweep.
 */
function keysOf<Shape>() {
  return <K extends readonly Extract<keyof Shape, string>[]>(
    keys: K,
    ..._missing: [Missing<Shape, K[number]>] extends [never]
      ? [] : [missingFromTheKeyList: Missing<Shape, K[number]>]
  ): K => keys;
}

// `{}` is the idiom here on purpose: "is a Pick of just this key satisfied by
// an object with nothing in it" is exactly the question, and only `{}` asks it.
// eslint-disable-next-line @typescript-eslint/no-empty-object-type
type RequiredKeysOf<T> = { [K in keyof T]-?: {} extends Pick<T, K> ? never : K }[keyof T] & string;
// eslint-disable-next-line @typescript-eslint/no-empty-object-type
type OptionalKeysOf<T> = { [K in keyof T]-?: {} extends Pick<T, K> ? K : never }[keyof T] & string;

/**
 * `keysOf` for a shape with optional fields: the first list is what always
 * travels, the second what may be absent - and absent is not `undefined`
 * (I6). Both are checked against the shape: a required field in the optional
 * list, or an optional one in the required list, fails on the argument, and
 * a field in neither fails on the call the way `keysOf` does. The runtime
 * sweep reads `keys` as the must-haves and `optional` as the may-haves.
 */
function shapeKeys<Shape>() {
  return <K extends readonly RequiredKeysOf<Shape>[], O extends readonly OptionalKeysOf<Shape>[]>(
    keys: K, optional: O,
    ..._missing: [Missing<Shape, K[number] | O[number]>] extends [never]
      ? [] : [missingFromTheKeyLists: Missing<Shape, K[number] | O[number]>]
  ): { readonly keys: K; readonly optional: O } => ({ keys, optional });
}

// ----------------------------------------------------------- the key lists

/** Byte-sorted, so a diff of this file reads like a diff of the shape. */

/**
 * `heldArmies` arrived with the surrender re-hire, after `save-v5.json` was
 * frozen: a kingdom that has never surrendered never carries the key, and a
 * save from before it is exactly such a kingdom, so it loads without a
 * migration rather than failing for one record nobody had. Optional keys
 * cannot go through `keysOf`, so this shape is asserted the way the town is.
 */
const PLAYER_STATE_SHAPE = shapeKeys<PlayerState>()([
  "colour", "defeated", "faction", "heroes", "id", "kind", "modifiers",
  "resources", "tavern", "townlessDays", "towns",
] as const, ["heldArmies"] as const);
export const PLAYER_STATE_KEYS = PLAYER_STATE_SHAPE.keys;
export const PLAYER_STATE_OPTIONAL_KEYS = PLAYER_STATE_SHAPE.optional;



export const HERO_STATE_KEYS = keysOf<HeroState>()([
  "army", "artifacts", "backpack", "boat", "def", "experience", "facing",
  "faction", "id", "inTown", "level", "mana", "modifiers", "movementLeft",
  "owner", "primary", "secondary", "spellbook", "visited", "x", "y", "z",
] as const);

/**
 * The fourth shape with an optional field. `researchedThisTurn` arrived with
 * The expansion spell research, after `save-v2.json` was frozen, so a town saved without
 * it must load rather than fail - and a required column would have forced a
 * format bump and a new fixture for one boolean.
 */
const TOWN_STATE_SHAPE = shapeKeys<TownState>()([
  "available", "built", "builtThisTurn", "faction", "garrison", "garrisonHero",
  "guildSpells", "id", "modifiers", "name", "owner", "visitingHero", "x", "y", "z",
] as const, ["autoBuild", "researchedThisTurn"] as const);
export const TOWN_STATE_KEYS = TOWN_STATE_SHAPE.keys;
export const TOWN_STATE_OPTIONAL_KEYS = TOWN_STATE_SHAPE.optional;



export const ARMY_STATE_KEYS = keysOf<ArmyState>()(["id", "owner", "slots"] as const);
export const CARAVAN_STATE_KEYS = keysOf<CaravanState>()([
  "arrivesOnDay", "count", "def", "id", "owner", "to", "x", "y", "z",
] as const);
export const ARMY_SLOT_KEYS = keysOf<ArmySlot>()(["count", "def"] as const);

export const MAP_OBJECT_STATE_KEYS = keysOf<MapObjectState>()([
  "def", "guard", "id", "kind", "owner", "payload", "removed", "visitedBy", "x", "y", "z",
] as const);

export const TURN_STATE_KEYS = keysOf<TurnState>()([
  "activePlayer", "day", "month", "order", "week",
] as const);

export const FOG_STATE_KEYS = keysOf<FogState>()(["memory", "seen"] as const);

export const ADVENTURE_MAP_KEYS = keysOf<AdventureMapState>()([
  "ground", "groundIndex",
  "height", "levels", "moveCost", "objectIndex", "occupant", "river", "road",
  "terrain", "variant", "width",
] as const);

export const VICTORY_KEYS = keysOf<GameState["victory"]>()([
  "condition", "over", "winner",
] as const);

export const PENDING_INTERACTION_KEYS = keysOf<PendingInteraction>()([
  "id", "legalResponses", "playerId", "prompt", "resumeToken",
] as const);

export const RESUME_TOKEN_KEYS = keysOf<ResumeToken>()(["data", "kind"] as const);

/**
 * The fifth shape with an optional field. `picks` - the subskill branches a
 * hero chose - arrived after `save-v5.json` was frozen, and a skill saved
 * before subskills existed carries no key rather than an empty array.
 */
const SECONDARY_SKILL_SHAPE = shapeKeys<SecondarySkillState>()(
  ["def", "level"] as const, ["picks"] as const);
export const SECONDARY_SKILL_KEYS = SECONDARY_SKILL_SHAPE.keys;
export const SECONDARY_SKILL_OPTIONAL_KEYS = SECONDARY_SKILL_SHAPE.optional;

export const PRIMARY_SKILL_KEYS = keysOf<PrimarySkills>()([
  "attack", "defense", "knowledge", "spellPower",
] as const);

/** The seven resources are a closed set: an eighth is a save format change, and
 *  `RESOURCES` is already the one place that says which seven. */
export const RESOURCE_KEYS = keysOf<Resources>()([...RESOURCES].sort());

/** `IdKind`, which has no exported array to derive from. */
export const COUNTER_KEYS = keysOf<GameState["counters"]>()([
  "army", "caravan", "hero", "interaction", "object", "stack", "town",
] as const);

/** The named streams (D5). Adding one is a format change for the same reason a
 *  new `PlayerState` field is: an old save has no state for it to resume from. */
export const RNG_STREAM_KEYS = keysOf<GameState["rng"]>()([...RNG_STREAMS].sort());

export const RNG_STREAM_STATE_KEYS = keysOf<RngStreamState>()([
  "hi", "incHi", "incLo", "lo",
] as const);

/**
 * A `Modifier` is the one persisted shape with optional fields, and absent is
 * not the same as `undefined` (I6): the runtime check requires the four that
 * always travel and merely permits the three that do not. `keysOf` cannot say
 * that in one list, so the two halves are asserted to cover the shape between
 * them instead.
 */
const MODIFIER_SHAPE = shapeKeys<Modifier>()(["op", "source", "stat", "value"] as const, ["expires", "stackGroup", "stackRule"] as const);
export const MODIFIER_KEYS = MODIFIER_SHAPE.keys;
export const MODIFIER_OPTIONAL_KEYS = MODIFIER_SHAPE.optional;



// --------------------------------------------------------------- battle side

/**
 * `wet` is the fifth optional shape: a dry board omits it so a grass fight
 * does not grow a 165-byte zero array. Present on swamp, river and boat
 * battles, and on any fight a Leviathan has flooded.
 */
const BATTLE_STATE_SHAPE = shapeKeys<BattleState>()([
  "heroes", "obstacles", "order", "outcome", "rng", "round", "siege", "stacks",
  "tacticsHexes", "tacticsPhase", "terrain", "turnIndex", "waited",
] as const, ["wet", "fire", "poison"] as const);
export const BATTLE_STATE_KEYS = BATTLE_STATE_SHAPE.keys;
export const BATTLE_STATE_OPTIONAL_KEYS = BATTLE_STATE_SHAPE.optional;



export const STACK_STORE_KEYS = keysOf<StackStore>()([
  "attack", "casts", "cooldown", "count", "damageMax", "damageMin", "def", "defense",
  "facing", "flags", "flags2", "hex", "killed", "luck", "maxHp", "mods", "morale", "n",
  "shots", "side", "slotInArmy", "speed", "strikes", "topHp",
] as const);

export const BATTLE_HERO_KEYS = keysOf<BattleHeroState>()([
  "army", "attack", "castThisRound", "defense", "id", "knowledge", "mana",
  "owner", "racialUsed", "spellPower", "spellbook", "wounds",
] as const);

/**
 * The second shape with an optional field, and for the same reason as
 * `Modifier`: a conjured obstacle carries the damage it deals and a wall does
 * not, and absent is not `undefined` (I6). Declared the same way, so a field
 * added to `Obstacle` and forgotten here is a compile error rather than a save
 * that quietly drops it.
 */
const OBSTACLE_SHAPE = shapeKeys<Obstacle>()(["art", "hex", "integrity", "kind"] as const, ["power"] as const);
export const OBSTACLE_KEYS = OBSTACLE_SHAPE.keys;
export const OBSTACLE_OPTIONAL_KEYS = OBSTACLE_SHAPE.optional;



export const BATTLE_SIEGE_KEYS = keysOf<NonNullable<BattleState["siege"]>>()([
  "active", "fortLevel", "gateOpen",
] as const);

// ---------------------------------------------------------- the runtime half

/**
 * Where each shape lives in a parsed save tree. `*` matches every key of a
 * record, `#` every element of a list; a `null` or an absent node is skipped,
 * so an optional shape (`battle`, `pending`, `siege`) costs no special case.
 */
export interface ShapeSpec {
  readonly path: readonly string[];
  /** Names the shape in the message: "player at $.state.players.player:red". */
  readonly what: string;
  readonly keys: readonly string[];
  readonly optional?: readonly string[];
}

export const PERSISTED_SHAPES: readonly ShapeSpec[] = [
  { path: ["state", "turn"], what: "turn", keys: TURN_STATE_KEYS },
  { path: ["state", "victory"], what: "victory", keys: VICTORY_KEYS },
  { path: ["state", "map"], what: "map", keys: ADVENTURE_MAP_KEYS },
  { path: ["state", "counters"], what: "counters", keys: COUNTER_KEYS },
  { path: ["state", "rng"], what: "rng", keys: RNG_STREAM_KEYS },
  { path: ["state", "rng", "*"], what: "rng stream", keys: RNG_STREAM_STATE_KEYS },
  { path: ["state", "fog", "*"], what: "fog", keys: FOG_STATE_KEYS },

  {
    path: ["state", "players", "*"], what: "player",
    keys: PLAYER_STATE_KEYS, optional: PLAYER_STATE_OPTIONAL_KEYS,
  },
  { path: ["state", "players", "*", "resources"], what: "resources", keys: RESOURCE_KEYS },

  { path: ["state", "heroes", "*"], what: "hero", keys: HERO_STATE_KEYS },
  { path: ["state", "heroes", "*", "primary"], what: "primary skills", keys: PRIMARY_SKILL_KEYS },
  {
    path: ["state", "heroes", "*", "secondary", "#"],
    what: "secondary skill", keys: SECONDARY_SKILL_KEYS,
    optional: SECONDARY_SKILL_OPTIONAL_KEYS,
  },

  {
    path: ["state", "towns", "*"], what: "town",
    keys: TOWN_STATE_KEYS, optional: TOWN_STATE_OPTIONAL_KEYS,
  },
  { path: ["state", "armies", "*"], what: "army", keys: ARMY_STATE_KEYS },
  { path: ["state", "armies", "*", "slots", "#"], what: "army slot", keys: ARMY_SLOT_KEYS },
  { path: ["state", "objects", "*"], what: "object", keys: MAP_OBJECT_STATE_KEYS },
  { path: ["state", "caravans", "*"], what: "caravan", keys: CARAVAN_STATE_KEYS },

  { path: ["state", "pending"], what: "pending interaction", keys: PENDING_INTERACTION_KEYS },
  { path: ["state", "pending", "resumeToken"], what: "resume token", keys: RESUME_TOKEN_KEYS },

  {
    path: ["state", "battle"], what: "battle",
    keys: BATTLE_STATE_KEYS, optional: BATTLE_STATE_OPTIONAL_KEYS,
  },
  { path: ["state", "battle", "stacks"], what: "battle stacks", keys: STACK_STORE_KEYS },
  { path: ["state", "battle", "heroes", "#"], what: "battle hero", keys: BATTLE_HERO_KEYS },
  {
    path: ["state", "battle", "obstacles", "#"], what: "obstacle",
    keys: OBSTACLE_KEYS, optional: OBSTACLE_OPTIONAL_KEYS,
  },
  { path: ["state", "battle", "siege"], what: "siege", keys: BATTLE_SIEGE_KEYS },
  { path: ["state", "battle", "rng", "*"], what: "battle rng stream", keys: RNG_STREAM_STATE_KEYS },

  ...(["players", "heroes", "towns"] as const).map((m): ShapeSpec => ({
    path: ["state", m, "*", "modifiers", "#"],
    what: "modifier",
    keys: MODIFIER_KEYS,
    optional: MODIFIER_OPTIONAL_KEYS,
  })),
  {
    path: ["state", "battle", "stacks", "mods", "#", "#"],
    what: "modifier", keys: MODIFIER_KEYS, optional: MODIFIER_OPTIONAL_KEYS,
  },
];

const isRecord = (v: unknown): v is Record<string, unknown> =>
  v !== null && typeof v === "object" && !Array.isArray(v);

/**
 * One issue per (shape, key) rather than one per offending record: a save with
 * two hundred heroes that all lost the same field is one mistake, and two
 * hundred lines of it would bury the rest of the report. The first path that
 * shows the problem is named, because that is the one someone will open.
 */
function checkShape(
  node: unknown, spec: ShapeSpec, path: string, issues: string[], seen: Set<string>,
): void {
  if (!isRecord(node)) {
    // A list or a scalar where a record belongs. `null` never reaches here.
    const key = `${spec.what}|type`;
    if (!seen.has(key)) {
      seen.add(key);
      issues.push(`${spec.what} at ${path}: not an object`);
    }
    return;
  }
  const allowed = new Set<string>([...spec.keys, ...(spec.optional ?? [])]);
  for (const k of Object.keys(node).sort()) {
    if (allowed.has(k)) continue;
    const id = `${spec.what}|extra|${k}`;
    if (seen.has(id)) continue;
    seen.add(id);
    issues.push(`${spec.what} at ${path}: unexpected key "${k}"`);
  }
  for (const k of spec.keys) {
    if (Object.prototype.hasOwnProperty.call(node, k)) continue;
    const id = `${spec.what}|missing|${k}`;
    if (seen.has(id)) continue;
    seen.add(id);
    issues.push(`${spec.what} at ${path}: missing key "${k}" (I13: needs a migration)`);
  }
}

function walk(
  node: unknown, spec: ShapeSpec, i: number, path: string,
  onShape: (node: unknown, spec: ShapeSpec, path: string) => void,
): void {
  if (node === null || node === undefined) return;
  if (i === spec.path.length) {
    onShape(node, spec, path);
    return;
  }
  const seg = spec.path[i] as string;
  if (seg === "#") {
    if (!Array.isArray(node)) return;
    const list = node as readonly unknown[];
    for (let j = 0; j < list.length; j++) {
      walk(list[j], spec, i + 1, `${path}[${String(j)}]`, onShape);
    }
    return;
  }
  if (!isRecord(node)) return;
  if (seg === "*") {
    for (const k of Object.keys(node).sort()) walk(node[k], spec, i + 1, `${path}.${k}`, onShape);
    return;
  }
  // A missing node is not this sweep's business: the root key set already
  // reported it, and reporting it twice makes the shorter list harder to read.
  if (!Object.prototype.hasOwnProperty.call(node, seg)) return;
  walk(node[seg], spec, i + 1, `${path}.${seg}`, onShape);
}

/**
 * Every persisted record in `raw` whose key set is not the one this build
 * compiles against, as text. Empty means a decoded `GameState` will have a
 * value at every field the types promise - which is the property that makes
 * "no undefined values in state" (I6) true of a *loaded* game and not only of a
 * freshly generated one.
 */
export function findShapeIssues(raw: unknown): string[] {
  const issues: string[] = [];
  const seen = new Set<string>();
  for (const spec of PERSISTED_SHAPES) {
    walk(raw, spec, 0, "$", (node, sp, path) => { checkShape(node, sp, path, issues, seen); });
  }
  return issues;
}

/**
 * Which specs found something, by path. A table of paths is only a guard where
 * the paths are right, and a typo (`resource` for `resources`) would disable
 * one silently - so the suite asserts coverage against a tree that holds one of
 * every shape rather than trusting the strings.
 */
export function shapePathsFound(raw: unknown): ReadonlySet<string> {
  const found = new Set<string>();
  for (const spec of PERSISTED_SHAPES) {
    walk(raw, spec, 0, "$", () => { found.add(spec.path.join(".")); });
  }
  return found;
}
